import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X, Scale, ArrowRight, DollarSign } from 'lucide-react';
import PricingModal from './PricingModal';

const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeSubCategory, setActiveSubCategory] = useState(null);
  const [pricingModalOpen, setPricingModalOpen] = useState(false);
  const [selectedPricingCategory, setSelectedPricingCategory] = useState(null);
  const location = useLocation();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
        setActiveSubCategory(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setActiveDropdown(null);
    setActiveSubCategory(null);
    // Scroll to top when location changes
    window.scrollTo(0, 0);
  }, [location]);

  const servicesMenu = {
    'Trademark Services': {
      services: [
        { name: 'Register Your Trademark', path: '/services/trademark/register', description: 'Secure your brand name and logo' },
        { name: 'Clearance Search Services', path: '/services/trademark/clearance', description: 'Verify trademark availability' },
        { name: 'Trademark Ownership Transfers', path: '/services/trademark/ownership-transfers', description: 'Transfer trademark rights' },
        { name: 'Managed Trademark Renewal', path: '/services/trademark/renewal', description: 'Keep your trademark active' },
        { name: 'Submission of Statements of Use', path: '/services/trademark/statement-of-use', description: 'File intent-to-use statements' },
        { name: 'Support and Response for Trademark Office Actions', path: '/services/trademark/office-actions', description: 'Handle USPTO responses' },
        { name: 'Active Monitoring for Trademark Infringement', path: '/services/trademark/monitoring', description: 'Protect against infringement' },
        { name: 'Support and Guidance for Trademark Cancellations', path: '/services/trademark/cancellations', description: 'Navigate cancellation proceedings' },
        { name: 'Legal Representation for Trademark Disputes', path: '/services/trademark/disputes', description: 'Expert legal representation' },
        { name: 'TTAB Petition', path: '/services/trademark/ttab', description: 'Trademark trial proceedings' },
        { name: 'Combatting Counterfeiting and Trademark Infringement', path: '/services/trademark/counterfeiting', description: 'Stop counterfeiters' },
      ],
      image: 'https://images.unsplash.com/photo-1562564055-71e051d33c19?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGRvY3VtZW50c3xlbnwwfHx8fDE3NjQwMjUzMjh8MA&ixlib=rb-4.1.0&q=85'
    },
    'Copyright Services': {
      services: [
        { name: 'Register Your Copyright', path: '/services/copyright/register', description: 'Protect your creative works' },
        { name: 'Assistance for Your Copyright Enforcement', path: '/services/copyright/enforcement', description: 'Enforce your copyright rights' },
        { name: 'Copyright Transfers and Licensing Support', path: '/services/copyright/transfers', description: 'License and transfer rights' },
        { name: 'Legal Representation for Your Copyright', path: '/services/copyright/litigation', description: 'Copyright litigation support' },
        { name: 'Management of Your Copyright Portfolio', path: '/services/copyright/portfolio', description: 'Manage multiple copyrights' },
        { name: 'Compliance with DMCA', path: '/services/copyright/dmca', description: 'Digital copyright protection' },
      ],
      image: 'https://images.unsplash.com/photo-1758518731462-d091b0b4ed0d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHw0fHxsZWdhbCUyMG9mZmljZXxlbnwwfHx8fDE3NjQwMjUzMTZ8MA&ixlib=rb-4.1.0&q=85'
    },
    'Patent Services': {
      services: [
        { name: 'Search for Existing Patent', path: '/services/patent/search', description: 'Prior art searches' },
        { name: 'Support and Response for Patent Office Actions', path: '/services/patent/office-actions', description: 'USPTO office action responses' },
        { name: 'Application Submission for Patent', path: '/services/patent/application', description: 'File patent applications' },
        { name: 'Legal Representation for Patent Protection', path: '/services/patent/litigation', description: 'Patent enforcement' },
      ],
      image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxpbm5vdmF0aW9uJTIwdGVjaG5vbG9neXxlbnwwfHx8fDE3NjQwMjUzNDB8MA&ixlib=rb-4.1.0&q=85'
    },
    'Company Formation': {
      services: [
        { name: 'Limited Liability Company (LLC)', path: '/services/company/llc', description: 'Form your LLC quickly' },
        { name: 'C Corporation', path: '/services/company/c-corp', description: 'Incorporate as C Corp' },
        { name: 'S Corporation', path: '/services/company/s-corp', description: 'S Corp formation' },
        { name: 'Non-Profit', path: '/services/company/non-profit', description: 'Start a non-profit organization' },
      ],
      image: 'https://images.unsplash.com/photo-1497366754035-f200968a6e72?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxsZWdhbCUyMG9mZmljZXxlbnwwfHx8fDE3NjQwMjUzMTZ8MA&ixlib=rb-4.1.0&q=85'
    },
    'Legal Services': {
      services: [
        { name: 'Business Immigration Law', path: '/services/legal/immigration', description: 'Immigration legal support' },
        { name: 'Due Diligence Process', path: '/services/legal/due-diligence', description: 'Legal due diligence' },
        { name: 'IT & Commercial Law', path: '/services/legal/it-commercial', description: 'Technology and commercial law' },
        { name: 'Legal Consultation on Partnerships & LLPs', path: '/services/legal/partnerships', description: 'Partnership legal advice' },
        { name: 'Data Protection & Privacy Law', path: '/services/legal/data-protection', description: 'Privacy compliance' },
      ],
      image: 'https://images.unsplash.com/photo-1528747008803-f9f5cc8f1a64?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBhdHRvcm5leXxlbnwwfHx8fDE3NjQwMjUzMzR8MA&ixlib=rb-4.1.0&q=85'
    },
    'Additional Services': {
      services: [
        { name: 'Amazon Brand Registry in the US', path: '/services/additional/amazon-brand-registry', description: 'Register on Amazon Brand Registry' },
        { name: 'Bookkeeping Service', path: '/services/additional/bookkeeping', description: 'Professional bookkeeping' },
        { name: 'Business Formation Services', path: '/services/additional/business-formation', description: 'Comprehensive formation services' },
        { name: 'Certificate of Good Standing', path: '/services/additional/good-standing', description: 'Obtain certificates' },
        { name: 'Company Dissolution', path: '/services/additional/dissolution', description: 'Close your business properly' },
        { name: 'Company Name Change', path: '/services/additional/name-change', description: 'Change company name' },
        { name: 'Company Renewal Services', path: '/services/additional/renewal', description: 'Annual renewals' },
        { name: 'Issue of Shares', path: '/services/additional/shares', description: 'Share issuance services' },
        { name: 'Nominee Director & Secretarial Services', path: '/services/additional/nominee', description: 'Nominee services' },
        { name: 'Tax Advisory & Filing', path: '/services/additional/tax', description: 'Tax planning and filing' },
        { name: 'Transfer of Shares', path: '/services/additional/transfer-shares', description: 'Share transfer services' },
        { name: 'CFO Services', path: '/services/additional/cfo', description: 'Chief Financial Officer services' },
      ],
      image: 'https://images.unsplash.com/photo-1562564055-71e051d33c19?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGRvY3VtZW50c3xlbnwwfHx8fDE3NjQwMjUzMjh8MA&ixlib=rb-4.1.0&q=85'
    },
  };

  const pricingPackages = {
    'Trademark Services': [
      {
        name: 'Essentials',
        price: '$45',
        period: '+ Federal Filing Fee',
        description: 'Perfect for first-time trademark filers',
        features: [
          'Case Review & Assessment',
          'Basic Trademark Search',
          'USPTO Filing Assistance',
          'Trademark Monitoring (1 Month)',
          'Email Support',
          'Attorney Pre-Filing Letter',
          'Trademark Secured'
        ]
      },
      {
        name: 'Standard',
        price: '$149',
        period: '+ Federal Filing Fee',
        description: 'Ideal for growing businesses',
        features: [
          'Everything in Essentials',
          'Expert Case Review',
          'Comprehensive Trademark Search',
          'Trademark Monitoring (3 Months)',
          'Risk Evaluation Report',
          'Cease & Desist Template',
          'Dedicated Account Manager',
          '100% Approval Guarantee'
        ],
        popular: true
      },
      {
        name: 'Professional',
        price: '$299',
        period: '+ Federal Filing Fee',
        description: 'Complete protection for established brands',
        features: [
          'Everything in Standard',
          'Attorney Review & Filing',
          '24-Hour Expedited Processing',
          'Trademark Monitoring (12 Months)',
          'Comprehensive Search (50 States)',
          'Office Action Support',
          'Opposition & Litigation Advice',
          '100% Approval Guarantee'
        ]
      },
      {
        name: 'Premium',
        price: '$899',
        period: '+ Federal Filing Fee',
        description: 'Maximum protection & premium service',
        features: [
          'Everything in Professional',
          'Direct Attorney Assignment',
          'Trademark Monitoring (24 Months)',
          'International Trademark Support',
          '24/7 Secure Portal Access',
          'Complete Opposition Management',
          'White Glove Service',
          '100% Approval Guarantee'
        ]
      },
    ],
    'Copyright Services': [
      {
        name: 'Basic',
        price: '$799',
        period: '/filing',
        description: 'Essential trademark protection',
        features: [
          'Comprehensive Trademark Search',
          'Single Class Registration',
          'USPTO Filing Included',
          'Basic Office Action Response',
          'Email Support'
        ]
      },
      {
        name: 'Professional',
        price: '$1,499',
        period: '/filing',
        description: 'Complete trademark solution',
        features: [
          'Everything in Basic',
          'Up to 3 Class Registrations',
          'Priority Filing Service',
          'Advanced Office Action Responses',
          'Trademark Monitoring (1 Year)',
          'Phone & Email Support'
        ],
        popular: true
      },
      {
        name: 'Enterprise',
        price: 'Custom',
        description: 'Full-service IP protection',
        features: [
          'Everything in Professional',
          'Unlimited Class Registrations',
          'International Filing Support',
          'Dedicated Account Manager',
          '24/7 Priority Support',
          'Litigation Support'
        ]
      },
    ],
    'Copyright Services': [
      {
        name: 'Cease & Desist',
        price: '$599',
        description: 'Attorney demand letter',
        features: [
          'Infringement Analysis',
          'Attorney Demand Letter',
          'Negotiation Support',
          'Settlement Documentation',
          '30-Day Follow-Up'
        ]
      },
      {
        name: 'DMCA + Enforcement',
        price: '$999',
        description: 'Comprehensive takedown',
        features: [
          'Everything in Cease & Desist',
          'DMCA Takedown Notices',
          'Multiple Platform Filing',
          'ISP Communications',
          'Litigation Threat Assessment',
          'Extended Negotiation'
        ],
        popular: true
      },
      {
        name: 'Litigation Package',
        price: 'Custom',
        description: 'Federal court filing',
        features: [
          'Everything in DMCA',
          'Federal Court Complaint',
          'Discovery Management',
          'Expert Witnesses',
          'Trial Preparation',
          'Full Litigation Support'
        ]
      },
    ],
    'Patent Services': [
      {
        name: 'Preliminary Search',
        price: '$799',
        description: 'Quick patentability check',
        features: [
          'USPTO Database Search',
          'U.S. Patents & Applications',
          'Basic Prior Art Report',
          'Patentability Opinion',
          '5-Day Turnaround'
        ]
      },
      {
        name: 'Comprehensive Search',
        price: '$1,999',
        description: 'Thorough prior art analysis',
        features: [
          'Everything in Preliminary',
          'International Patents',
          'Non-Patent Literature',
          'Detailed Claim Analysis',
          'Strategic Recommendations',
          'Patent Attorney Review'
        ],
        popular: true
      },
      {
        name: 'FTO + Patentability',
        price: '$3,999',
        description: 'Complete IP clearance',
        features: [
          'Everything in Comprehensive',
          'Freedom-to-Operate Search',
          'Infringement Risk Analysis',
          'Design-Around Options',
          'Licensing Opportunities',
          'Strategic Consultation'
        ]
      },
    ],
    'Company Formation': [
      {
        name: 'Basic LLC',
        price: '$499',
        description: 'Essential LLC formation',
        features: [
          'Articles of Organization',
          'State Filing',
          'EIN Application',
          'Basic Operating Agreement',
          'Membership Certificates'
        ]
      },
      {
        name: 'Complete LLC',
        price: '$999',
        description: 'Full LLC package',
        features: [
          'Everything in Basic',
          'Custom Operating Agreement',
          'Initial Resolutions',
          'Registered Agent Service (1 Year)',
          'Compliance Calendar',
          'Attorney Review'
        ],
        popular: true
      },
      {
        name: 'Multi-State LLC',
        price: '$1,999',
        description: 'Multiple state registration',
        features: [
          'Everything in Complete',
          'Foreign Qualification',
          'Up to 3 Additional States',
          'Multi-State Compliance',
          'Registered Agent (All States)',
          'Ongoing Support'
        ]
      },
    ],
    'Legal Services': [
      {
        name: 'Basic Consultation',
        price: '$299',
        period: '/hour',
        description: 'Legal advisory services',
        features: [
          'Initial Consultation',
          'Legal Review',
          'Document Analysis',
          'Strategic Advice',
          'Email Support'
        ]
      },
      {
        name: 'Comprehensive Package',
        price: '$1,999',
        description: 'Full legal support',
        features: [
          'Everything in Basic',
          'Contract Drafting',
          'Compliance Review',
          'Due Diligence',
          'Ongoing Support',
          'Priority Response'
        ],
        popular: true
      },
      {
        name: 'Enterprise Legal',
        price: 'Custom',
        description: 'Complete legal partnership',
        features: [
          'Everything in Comprehensive',
          'Dedicated Legal Team',
          'Retainer Agreement',
          'Unlimited Consultations',
          '24/7 Access',
          'Litigation Support'
        ]
      },
    ],
    'Additional Services': [
      {
        name: 'Essential',
        price: '$199',
        description: 'Basic corporate services',
        features: [
          'Document Filing',
          'Certificate Processing',
          'Basic Compliance',
          'Email Support'
        ]
      },
      {
        name: 'Professional',
        price: '$499',
        description: 'Comprehensive services',
        features: [
          'Everything in Essential',
          'Monthly Bookkeeping',
          'Tax Advisory',
          'Compliance Management',
          'Priority Support'
        ],
        popular: true
      },
      {
        name: 'Enterprise',
        price: 'Custom',
        description: 'Full-service corporate support',
        features: [
          'Everything in Professional',
          'CFO Services',
          'Strategic Planning',
          'Dedicated Account Manager',
          'Custom Solutions'
        ]
      },
    ],
  };

  const getButtonText = (category) => {
    const buttonTexts = {
      'Trademark Services': 'Get Trademark Pricing',
      'Copyright Services': 'Get Copyright Pricing',
      'Patent Services': 'Get Patent Pricing',
      'Company Formation': 'Get Formation Pricing',
      'Legal Services': 'Get Legal Pricing',
      'Additional Services': 'View Service Pricing'
    };
    return buttonTexts[category] || 'View Pricing';
  };

  const handlePricingClick = (category) => {
    setSelectedPricingCategory(category);
    setPricingModalOpen(true);
    setActiveDropdown(null);
    setActiveSubCategory(null);
  };

  const menuItems = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Services', hasMegaMenu: true },
    {
      name: 'Media Center',
      dropdown: [
        { name: 'Blogs', path: '/resources/blogs' },
        { name: 'News & Updates', path: '/resources/news' },
        { name: 'Publications', path: '/resources/publications' },
      ],
    },
    { name: 'Contact Us', path: '/contact' },
  ];

  return (
    <nav className="bg-slate-900 text-white sticky top-0 z-50 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img src="/assets/img/brand/logo-light.png" alt="Federal Trademark Experts" className="h-10" />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center space-x-1" ref={dropdownRef}>
            {menuItems.map((item, index) => (
              <div key={index} className="relative">
                {item.hasMegaMenu ? (
                  <div>
                    <button
                      onClick={() => setActiveDropdown(activeDropdown === index ? null : index)}
                      onMouseEnter={() => setActiveDropdown(index)}
                      className="px-3 py-2 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors flex items-center"
                    >
                      {item.name}
                      <ChevronDown className="ml-1 h-4 w-4" />
                    </button>
                    {activeDropdown === index && (
                      <div 
                        className="fixed left-0 right-0 top-16 w-full bg-white shadow-2xl text-slate-900 z-50"
                        onMouseLeave={() => {
                          setActiveDropdown(null);
                          setActiveSubCategory(null);
                        }}
                      >
                        <div className="max-w-7xl mx-auto">
                          <div className="grid grid-cols-12 gap-0">
                            {/* Left Side - Main Services */}
                            <div className="col-span-3 bg-slate-50 p-6 border-r border-slate-200">
                            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4">All Services</h3>
                            <div className="space-y-1">
                              {Object.keys(servicesMenu).map((category, catIndex) => (
                                <button
                                  key={catIndex}
                                  onMouseEnter={() => setActiveSubCategory(category)}
                                  className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 flex items-center justify-between group ${
                                    activeSubCategory === category || (activeSubCategory === null && catIndex === 0)
                                      ? 'bg-blue-600 text-white'
                                      : 'text-slate-700 hover:bg-slate-200'
                                  }`}
                                >
                                  <span>{category}</span>
                                  <ArrowRight className={`h-4 w-4 ${activeSubCategory === category || (activeSubCategory === null && catIndex === 0) ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`} />
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Middle - Inner Services */}
                          <div className="col-span-5 p-6 max-h-96 overflow-y-auto">
                            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4">
                              {activeSubCategory || Object.keys(servicesMenu)[0]}
                            </h3>
                            <div className="space-y-1">
                              {servicesMenu[activeSubCategory || Object.keys(servicesMenu)[0]].services.map((service, serviceIndex) => (
                                <Link
                                  key={serviceIndex}
                                  to={service.path}
                                  className="block px-3 py-2.5 rounded-md hover:bg-slate-100 transition-colors group"
                                >
                                  <div className="font-medium text-slate-900 text-sm group-hover:text-blue-600 transition-colors mb-0.5">
                                    {service.name}
                                  </div>
                                  <div className="text-xs text-slate-500">
                                    {service.description}
                                  </div>
                                </Link>
                              ))}
                            </div>
                          </div>

                            {/* Right Side - Image */}
                            <div className="col-span-4 bg-slate-100 p-6 flex flex-col justify-center">
                            <img
                              src={servicesMenu[activeSubCategory || Object.keys(servicesMenu)[0]].image}
                              alt={activeSubCategory || Object.keys(servicesMenu)[0]}
                              className="w-full h-64 object-cover rounded-lg shadow-md mb-4"
                            />
                            <h3 className="text-lg font-bold text-slate-900 mb-2">
                              {activeSubCategory || Object.keys(servicesMenu)[0]}
                            </h3>
                            <p className="text-sm text-slate-600 mb-4">
                              Expert legal services tailored to protect your intellectual property and business interests.
                            </p>
                            <Link
                              to={servicesMenu[activeSubCategory || Object.keys(servicesMenu)[0]].services[0].path}
                              className="inline-flex items-center text-blue-600 hover:text-blue-700 font-semibold text-sm mb-4"
                            >
                              Explore all services
                              <ArrowRight className="ml-1 h-4 w-4" />
                            </Link>
                            
                            {/* Pricing Button */}
                            <button
                              onClick={() => handlePricingClick(activeSubCategory || Object.keys(servicesMenu)[0])}
                              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-md transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl"
                            >
                              <DollarSign className="h-5 w-5" />
                              <span>{getButtonText(activeSubCategory || Object.keys(servicesMenu)[0])}</span>
                            </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : item.dropdown ? (
                  <div>
                    <button
                      onClick={() => setActiveDropdown(activeDropdown === index ? null : index)}
                      className="px-3 py-2 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors flex items-center"
                    >
                      {item.name}
                      <ChevronDown className="ml-1 h-4 w-4" />
                    </button>
                    {activeDropdown === index && (
                      <div className="absolute left-0 mt-2 w-64 bg-white rounded-md shadow-xl py-2 text-slate-900">
                        {item.dropdown.map((subItem, subIndex) => (
                          <Link
                            key={subIndex}
                            to={subItem.path}
                            className="block px-4 py-2 text-sm hover:bg-slate-100 transition-colors"
                          >
                            {subItem.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.path}
                    className="px-3 py-2 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors"
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 rounded-md hover:bg-slate-800 transition-colors"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-slate-800 border-t border-slate-700">
          <div className="px-2 pt-2 pb-3 space-y-1 max-h-96 overflow-y-auto">
            {menuItems.map((item, index) => (
              <div key={index}>
                {item.hasMegaMenu ? (
                  <div>
                    <button
                      onClick={() => setActiveDropdown(activeDropdown === index ? null : index)}
                      className="w-full text-left px-3 py-2 rounded-md text-base font-medium hover:bg-slate-700 transition-colors flex justify-between items-center"
                    >
                      {item.name}
                      <ChevronDown className={`h-4 w-4 transition-transform ${activeDropdown === index ? 'rotate-180' : ''}`} />
                    </button>
                    {activeDropdown === index && (
                      <div className="pl-2 space-y-2 mt-2">
                        {Object.keys(servicesMenu).map((category, catIndex) => (
                          <div key={catIndex}>
                            <button
                              onClick={() => setActiveSubCategory(activeSubCategory === category ? null : category)}
                              className="w-full text-left px-3 py-2 rounded-md text-sm font-semibold hover:bg-slate-700 transition-colors flex justify-between items-center text-blue-300"
                            >
                              {category}
                              <ChevronDown className={`h-3 w-3 transition-transform ${activeSubCategory === category ? 'rotate-180' : ''}`} />
                            </button>
                            {activeSubCategory === category && (
                              <div className="pl-4 space-y-1 mt-1">
                                {servicesMenu[category].services.map((service, serviceIndex) => (
                                  <Link
                                    key={serviceIndex}
                                    to={service.path}
                                    className="block px-3 py-2 rounded-md text-xs hover:bg-slate-700 transition-colors"
                                  >
                                    {service.name}
                                  </Link>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : item.dropdown ? (
                  <div>
                    <button
                      onClick={() => setActiveDropdown(activeDropdown === index ? null : index)}
                      className="w-full text-left px-3 py-2 rounded-md text-base font-medium hover:bg-slate-700 transition-colors flex justify-between items-center"
                    >
                      {item.name}
                      <ChevronDown className={`h-4 w-4 transition-transform ${activeDropdown === index ? 'rotate-180' : ''}`} />
                    </button>
                    {activeDropdown === index && (
                      <div className="pl-4 space-y-1 mt-1">
                        {item.dropdown.map((subItem, subIndex) => (
                          <Link
                            key={subIndex}
                            to={subItem.path}
                            className="block px-3 py-2 rounded-md text-sm hover:bg-slate-700 transition-colors"
                          >
                            {subItem.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.path}
                    className="block px-3 py-2 rounded-md text-base font-medium hover:bg-slate-700 transition-colors"
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Pricing Modal */}
      <PricingModal
        isOpen={pricingModalOpen}
        onClose={() => setPricingModalOpen(false)}
        category={selectedPricingCategory}
        pricingData={selectedPricingCategory ? pricingPackages[selectedPricingCategory] : []}
      />
    </nav>
  );
};

export default Navigation;